import React, { useEffect, useState } from 'react';
import JobFormPage from '../JobFormPage';
import FormField from '../../FormField';
import { RequestType, DayOfWeekSelections, BusinessJobTypes, JobTimes } from 'parent-portal/forms/JobRequestTypes';
import { FormFieldProps } from '../../FormTypes';
import JobDayPicker from './JobDayPicker';
import JobTimePicker from './JobTimePicker';
import JobMultiTimePicker from './JobMultiTimePicker';
import JobSameTimePicker from './JobSameTimePicker';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import BusinessJobDateRangePicker from './BusinessJobDateRangePicker';
import { Text } from 'library';
import JobDatePicker from './JobDatePicker';
import { isSameDay } from 'date-fns';
import { useUserContext } from 'UserContext';
import moment from 'moment';

export default function BusinessDateAndTimePage({
    businessJobType,
    requestType,
    daysOfTheWeek,
    jobDate,
    sameTimes,
    jobTimes,
    startDate,
    endDate,
}: {
    businessJobType: FormFieldProps<BusinessJobTypes | undefined>;
    requestType: FormFieldProps<RequestType>;
    daysOfTheWeek: FormFieldProps<DayOfWeekSelections>;
    jobDate: FormFieldProps<moment.Moment | null>;
    sameTimes: FormFieldProps<boolean>;
    jobTimes: FormFieldProps<JobTimes>;
    startDate: FormFieldProps<moment.Moment | null>;
    endDate: FormFieldProps<moment.Moment | null>;
}) {
    const [isValid, setIsValid] = useState(false);

    const { goToNext } = useJobFormContext();
    const { getDisplayText } = useUserContext();

    const trialRun = businessJobType.value === 'TRIAL_RUN';
    const subJob = businessJobType.value === 'SUBSTITUTE';

    useEffect(() => {
        const validTimes = jobTimes.isValid || (!sameTimes.value && daysOfTheWeek.isValid);
        const multiDay = Object.entries(daysOfTheWeek.value).filter((day) => day[1].selected).length > 1;
        const validTrialRun =
            validTimes && !!startDate.value && !!startDate.isValid && startDate.value.isSameOrAfter(moment(), 'date');

        const validSubJob =
            validTimes &&
            !!startDate.value &&
            !!endDate.value &&
            startDate.isValid &&
            endDate.isValid &&
            startDate.value.isSameOrAfter(moment(), 'date') &&
            (!multiDay || startDate.value < endDate.value);

        if (trialRun) setIsValid(validTrialRun);
        if (subJob) setIsValid(validSubJob);

        requestType.setValue(startDate.value !== endDate.value ? 'recurring' : 'onetime');
    }, [jobTimes, daysOfTheWeek, startDate, endDate, sameTimes.value, trialRun, subJob, requestType]);

    return (
        <JobFormPage title="When is your job?" onNextClicked={() => goToNext()} backText="Back" nextDisabled={!isValid}>
            <FormField
                title={subJob ? 'What are the dates/times that you need?' : 'When does the trial run start?'}
                style={{ marginTop: 30 }}
            >
                {subJob ? (
                    <Text variant="body2" textStyle={{ marginTop: 10 }}>
                        {getDisplayText('substitute').capitalize()} shifts can be one single day or multiple days.
                    </Text>
                ) : null}
                {trialRun ? (
                    <FormField style={{ marginTop: 30 }}>
                        <JobDatePicker jobDate={startDate} />
                    </FormField>
                ) : (
                    <FormField title={'Pick the dates.'} style={{ marginTop: 30 }}>
                        <BusinessJobDateRangePicker startDate={startDate} endDate={endDate} />
                    </FormField>
                )}
            </FormField>

            {isSameDay(startDate.value?.toDate() ?? new Date(), endDate.value?.toDate() ?? new Date()) &&
            !trialRun ? null : (
                <FormField title={'Choose the days of the week.'} style={{ marginTop: 30 }}>
                    <JobDayPicker daysOfTheWeek={daysOfTheWeek} />
                </FormField>
            )}
            <FormField title={'Select the times.'} style={{ marginTop: 30 }}>
                <JobSameTimePicker isSameTime={sameTimes} daysOfTheWeek={daysOfTheWeek} />
                {sameTimes.value ? (
                    <JobTimePicker
                        jobTimes={jobTimes}
                        startDate={startDate.value}
                        jobDate={jobDate.value}
                        allowPastMidnight={false}
                    />
                ) : (
                    <JobMultiTimePicker daysOfTheWeek={daysOfTheWeek} />
                )}
            </FormField>
        </JobFormPage>
    );
}
